import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Grid, TextField, Button, Divider, Alert, CircularProgress, IconButton, Tooltip, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { Save as SaveIcon, Refresh as RefreshIcon, Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { deleteData, getData, postData } from "../../../services/axiosInstance.js";
import { API_ENDPOINTS } from "../../../services/endpoints.js";

const TaxSlabEditor = ({ value, onChange, label, isSurcharge = false }) => {
    const slabs = Array.isArray(value) ? value : [];
    const limitField = isSurcharge ? "threshold" : "upTo";

    const handleSlabChange = (index, field, val) => {
        const updated = [...slabs];
        let finalVal = val === "" && field === limitField ? null : Number(val);
        if (field === "rate") finalVal = Number(val);

        updated[index] = { ...updated[index], [field]: finalVal };
        onChange(updated);
    };

    const addSlab = () => {
        const lastLimit = slabs.length > 0 ? (slabs[slabs.length - 1][limitField] || 1500000) : 0;
        onChange([...slabs, { [limitField]: lastLimit + 300000, rate: 0.1 }]);
    };

    const removeSlab = (index) => {
        onChange(slabs.filter((_, i) => i !== index));
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fcfcfc" }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {label} - Slab Configuration
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>{isSurcharge ? "Threshold (₹)" : "Up To (₹)"}</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Tax Rate (%)</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {slabs.map((slab, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type={slab[limitField] === null ? "text" : "number"}
                                        value={slab[limitField] === null ? "No Limit" : slab[limitField]}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v.toLowerCase() === 'no limit' || v === '') {
                                                handleSlabChange(idx, limitField, "");
                                            } else {
                                                handleSlabChange(idx, limitField, v);
                                            }
                                        }}
                                        variant="standard"
                                        fullWidth
                                        placeholder="Enter amount or 'No Limit'"
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={slab.rate}
                                        onChange={(e) => handleSlabChange(idx, "rate", e.target.value)}
                                        variant="standard"
                                        fullWidth
                                        helperText={`${(slab.rate * 100).toFixed(0)}%`}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="error" onClick={() => removeSlab(idx)}>
                                        <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {slabs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <Typography variant="caption">No slabs defined. Add one to start.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={addSlab}
                sx={{ mt: 1 }}
            >
                Add Slab
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Tip: Leave '{isSurcharge ? 'Threshold' : 'Up To'}' empty or type 'No Limit' for the highest slab.
            </Typography>
        </Paper>
    );
};

const UpdateConfirmationDialog = ({ open, onConfirm, onCancel, data }) => {
    if (!data) return null;
    const { key, oldValue, newValue, description } = data;

    const formatValue = (val) => {
        if (typeof val === 'object') return JSON.stringify(val, null, 2);
        return String(val);
    };

    return (
        <AppDialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: "bold", bgcolor: "#fff3e0", color: "#e65100" }}>
                Confirm Update: {key}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" gutterBottom>
                    Are you sure you want to update <strong>{description || key}</strong>?
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                            <Typography variant="caption" color="text.secondary" display="block">OLD VALUE</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5, color: "text.primary" }}>
                                {formatValue(oldValue)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: "#e8f5e9", borderColor: "#81c784" }}>
                            <Typography variant="caption" color="success.main" display="block" fontWeight="bold">NEW VALUE</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5, fontWeight: "bold" }}>
                                {formatValue(newValue)}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={onConfirm} variant="contained" color="warning" startIcon={<SaveIcon />}>
                    Yes, Update Now
                </Button>
            </DialogActions>
        </AppDialog>
    );
};

function PayrollSettings() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;
    const [constants, setConstants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [newItem, setNewItem] = useState({
        key: "",
        value: "",
        section: "PAYROLL_CONSTANTS",
        description: "",
    });

    const [confirmData, setConfirmData] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [originalConstants, setOriginalConstants] = useState([]);

    const [breakdowns, setBreakdowns] = useState([]);
    const [openBreakdownDialog, setOpenBreakdownDialog] = useState(false);
    const [currentBreakdown, setCurrentBreakdown] = useState({
        id: null,
        section: "80C",
        item_name: "",
        description: "",
        is_active: true
    });

    const handleOpenDialog = (section = "PAYROLL_CONSTANTS") => {
        setNewItem(prev => ({ ...prev, section }));
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNewItem({ key: "", value: "", section: "PAYROLL_CONSTANTS", description: "" });
    };

    const handleDelete = async (key) => {
        if (!window.confirm(`Are you sure you want to delete ${key}?`)) return;

        setSaving(true);
        try {
            const response = await postData(API_ENDPOINTS.deletePayrollCompensationConstants, key);
            if (response.status === "SUCCESS") {
                setSuccess(`Deleted ${key} successfully`);
                fetchConstants();
            } else {
                setError(response.message || "Failed to delete");
            }
        } catch (err) {
            setError("Error deleting constant");
        } finally {
            setSaving(false);
        }
    };

    const handleAddNew = async () => {
        if (!newItem.key || newItem.value === "") {
            setError("Key and Value are required");
            return;
        }

        const formattedKey = newItem.key.toUpperCase().replace(/\s+/g, "_");

        await handleUpdate(formattedKey, newItem.value, newItem.section.toUpperCase());
        handleCloseDialog();
    };

    const fetchConstants = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getPayrollCompensationConstants);
            if (response.status === "SUCCESS") {
                setConstants(response.result);
                setOriginalConstants(JSON.parse(JSON.stringify(response.result))); // Deep copy

                // Logic for restoreSession moved here or called here
                const saved = localStorage.getItem("unsaved_payroll_constants");
                if (saved) {
                    try {
                        const parsed = JSON.parse(saved);
                        if (parsed && Array.isArray(parsed) && parsed.length > 0) {
                            if (window.confirm("You have unsaved changes from a previous session. Would you like to restore them?")) {
                                setConstants(parsed);
                                setIsDirty(true);
                            } else {
                                localStorage.removeItem("unsaved_payroll_constants");
                            }
                        }
                    } catch (e) {
                       return null;
                    }
                }
            } else {
                setError("Failed to fetch constants");
            }
        } catch (err) {
            setError("Error connecting to server");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBreakdowns = useCallback(async () => {
        try {
            const response = await getData(API_ENDPOINTS.getAdminTaxBreakdowns);
            if (response.statusCode === 200) {
                setBreakdowns(response.result);
            }
        } catch (err) {
           return null;
        }
    }, []);

    useEffect(() => {
        fetchConstants();
        fetchBreakdowns();
    }, [fetchConstants, fetchBreakdowns]);

    useEffect(() => {
        if (constants.length > 0 && isDirty) {
            localStorage.setItem("unsaved_payroll_constants", JSON.stringify(constants));
        }
    }, [constants, isDirty]);

    const handleUpdate = async (key, value, section, description = null) => {
        if (!openConfirm) {
            const oldValue = originalConstants.find(c => c.key === key)?.value;
            const desc = description || constants.find(c => c.key === key)?.description || key;

            setConfirmData({
                key,
                oldValue,
                newValue: value,
                section,
                description: desc
            });
            setOpenConfirm(true);
            return;
        }

        setOpenConfirm(false);
        setSaving(true);
        setError(null);
        setSuccess(null);

        let valueToSave = value;

        // Try to parse if it looks like JSON array/object string
        if (typeof value === 'string' && (value.trim().startsWith('[') || value.trim().startsWith('{'))) {
            try {
                valueToSave = JSON.parse(value);
            } catch (e) {
                setError("Invalid JSON format for " + key);
                setSaving(false);
                return;
            }
        }

        try {
            const response = await postData(API_ENDPOINTS.updatePayrollCompensationConstants, {
                key,
                value: valueToSave,
                section,
                description: description || constants.find(c => c.key === key)?.description,
                hrId
            });
            if (response.status === "SUCCESS") {
                setSuccess(`Updated ${key} successfully`);
                // Update local original copy to match current
                setOriginalConstants((prev) =>
                    prev.map(c => c.key === key ? { ...c, value: valueToSave, section, description: description || c.description } : c)
                );
                // Clean up dirty state if all match (simplified: just clear it for now)
                setIsDirty(false);
                localStorage.removeItem("unsaved_payroll_constants");
                fetchConstants();
            } else {
                setError(response.message || "Failed to update");
            }
        } catch (err) {
            setError("Error updating constant");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveBreakdown = async () => {
        setSaving(true);
        try {
            const response = await postData(API_ENDPOINTS.manageTaxBreakdown, currentBreakdown);
            if (response.statusCode === 200 || response.statusCode === 201) {
                setSuccess("Breakdown saved successfully");
                fetchBreakdowns();
                setOpenBreakdownDialog(false);
            } else {
                setError(response.message || "Failed to save breakdown");
            }
        } catch (err) {
            setError("Error saving breakdown");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBreakdown = async (id) => {
        if (!window.confirm("Are you sure you want to delete this specific investment type?")) return;
        setSaving(true);
        try {
            const response = await deleteData(API_ENDPOINTS.deleteTaxBreakdownById(id));
            if (response.statusCode === 200) {
                setSuccess("Breakdown deleted successfully");
                fetchBreakdowns();
            } else {
                setError("Failed to delete breakdown");
            }
        } catch (err) {
            setError("Error deleting breakdown");
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (key, newValue, newDescription = null) => {
        setConstants((prev) =>
            prev.map((c) => (c.key === key ? { ...c, value: newValue, description: newDescription !== null ? newDescription : c.description } : c))
        );
        setIsDirty(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Group constants by section and maintain stable order of sections
    const sectionData = (Array.isArray(constants) ? constants : []).reduce((acc, curr) => {
        if (!curr || !curr.section) return acc;
        if (!acc.groups[curr.section]) {
            acc.groups[curr.section] = [];
            acc.order.push(curr.section);
        }
        acc.groups[curr.section].push(curr);
        return acc;
    }, { groups: {}, order: [] });

    const sections = sectionData.groups;
    const orderedSectionNames = sectionData.order;

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Payroll Configuration
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Manage global payroll constants, tax limits, and deduction rates.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchConstants} variant="outlined" size="small">Refresh</Button>
                    <Button startIcon={<AddIcon />} onClick={() => handleOpenDialog()} variant="contained" size="small">Add Constant</Button>
                </Box>
            </Box>

            {/* Tax Declaration Data Flow Guide */}
            <Alert severity="info" sx={{ mb: 4, border: '1px solid #b3e5fc' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Tax Declaration Logic (How values reach the Employee):
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1, border: '1px dashed #0288d1' }}>
                            <Typography variant="caption" fontWeight="bold" color="primary" display="block">1. DEFINE SECTION (Parent)</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                Add an entry in the <b>INVESTMENT_TYPES</b> card below. This creates the header on the employee page.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h5">➔</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1, border: '1px dashed #0288d1' }}>
                            <Typography variant="caption" fontWeight="bold" color="primary" display="block">2. ADD DROPDOWN ITEMS (Child)</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                Go to <b>Investment Type Breakdown</b> (at the very bottom). Add items like LIC/PPF and link them to the section.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Alert>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Grid container spacing={3}>
                {orderedSectionNames.map((sectionName) => {
                    const isInvestmentSection = sectionName === "INVESTMENT_TYPES";

                    return (
                        <Grid size={{ xs: 12 }} key={sectionName}>
                            <Paper elevation={0} sx={{ p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                        {sectionName.replace(/_/g, " ")}
                                    </Typography>
                                    <Tooltip title={`Add to ${sectionName}`}>
                                        <IconButton size="small" onClick={() => handleOpenDialog(sectionName)}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                {isInvestmentSection ? (
                                    /* Specialized Table view for Investment Types */
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Section Key</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Display Label (Employee Portal)</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sections[sectionName].map((item) => (
                                                    <TableRow key={item.key}>
                                                        <TableCell>{item.key.replace("INVESTMENT_TYPES_", "")}</TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                variant="standard"
                                                                value={item.description || ""}
                                                                onChange={(e) => handleChange(item.key, item.value, e.target.value)}
                                                                placeholder="Enter Section Label"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleUpdate(item.key, item.value, item.section, item.description)}
                                                                    color="primary"
                                                                    disabled={saving}
                                                                >
                                                                    <SaveIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDelete(item.key)}
                                                                    color="error"
                                                                    disabled={saving}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    /* Standard Grid view for other constants */
                                    <Grid container spacing={3}>
                                        {sections[sectionName].map((item) => {
                                            const isTaxSlab = item.key.startsWith('TAX_SLABS') || item.key.startsWith('SURCHARGE_SLABS');
                                            const isSurcharge = item.key.startsWith('SURCHARGE_SLABS');
                                            const isJson = isTaxSlab || (typeof item.value === 'object' && item.value !== null);

                                            return (
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }} sm={isJson ? 12 : 6} md={isJson ? 12 : 4} key={item.key}>
                                                    <Box sx={{ position: 'relative' }}>
                                                        {isTaxSlab ? (
                                                            <TaxSlabEditor
                                                                label={item.description || item.key}
                                                                value={item.value}
                                                                onChange={(val) => handleChange(item.key, val)}
                                                                isSurcharge={isSurcharge}
                                                            />
                                                        ) : (
                                                            <TextField
                                                                fullWidth
                                                                label={item.description || item.key}
                                                                value={isJson && typeof item.value === 'object' ? JSON.stringify(item.value, null, 2) : item.value}
                                                                onChange={(e) => {
                                                                    let val;
                                                                    if (!isJson) {
                                                                        val = item.key.includes('RATE') || typeof item.value === 'number' ? parseFloat(e.target.value) : e.target.value;
                                                                    } else {
                                                                        val = e.target.value;
                                                                    }
                                                                    handleChange(item.key, val);
                                                                }}
                                                                variant="outlined"
                                                                size="small"
                                                                multiline={isJson}
                                                                rows={isJson ? 6 : 1}
                                                                type={!isJson && typeof item.value === 'number' ? 'number' : 'text'}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <Box sx={{ display: 'flex' }}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleUpdate(item.key, item.value, item.section)}
                                                                                disabled={saving}
                                                                                color="primary"
                                                                            >
                                                                                <SaveIcon size="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleDelete(item.key)}
                                                                                disabled={saving}
                                                                                color="error"
                                                                            >
                                                                                <DeleteIcon size="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )
                                                                }}
                                                            />
                                                        )}

                                                        {/* Special Save button for Tax Slabs since we don't have InputProps there */}
                                                        {isTaxSlab && (
                                                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<SaveIcon />}
                                                                    variant="contained"
                                                                    onClick={() => handleUpdate(item.key, item.value, item.section)}
                                                                    disabled={saving}
                                                                >
                                                                    Save Slab Changes
                                                                </Button>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDelete(item.key)}
                                                                    disabled={saving}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon size="small" />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Confirmation Dialog */}
            <UpdateConfirmationDialog
                open={openConfirm}
                onConfirm={() => handleUpdate(confirmData.key, confirmData.newValue, confirmData.section, confirmData.description)}
                onCancel={() => {
                    setOpenConfirm(false);
                    setConfirmData(null);
                }}
                data={confirmData}
            />

            {/* Add New Constant Dialog */}
            <AppDialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'bold' }}>Add New Payroll Constant</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Constant Key (e.g. MEAL_ALLOWANCE)"
                                value={newItem.key}
                                onChange={(e) => setNewItem({ ...newItem, key: e.target.value })}
                                variant="outlined"
                                placeholder="USE_CAPS_AND_UNDERSCORES"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Value"
                                value={newItem.value}
                                onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                                variant="outlined"
                                helperText="Enter a number, string, or valid JSON array/object"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Section"
                                value={newItem.section}
                                onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
                                variant="outlined"
                                placeholder="PAYROLL_CONSTANTS / ALLOWANCE_RATES"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={newItem.description}
                                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                                variant="outlined"
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleAddNew}
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                    >
                        Create Constant
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* Investment Type Breakdown Management */}
            <Box sx={{ mt: 5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Investment Type Breakdown
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Manage individual items that appear in the "Investment Type" dropdown for each tax section.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setCurrentBreakdown({ id: null, section: "80C", item_name: "", description: "", is_active: true });
                            setOpenBreakdownDialog(true);
                        }}
                    >
                        Add New Item
                    </Button>
                </Box>

                <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                    <TableCell sx={{ fontWeight: "bold" }}>Parent Section (from Investment Types)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Item Name (in Employee Dropdown)</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Description / Notes</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {breakdowns.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No breakdown items found. Add some to populate dropdowns.</TableCell>
                                    </TableRow>
                                ) : (
                                    breakdowns.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>{item.section}</TableCell>
                                            <TableCell>{item.item_name}</TableCell>
                                            <TableCell>{item.description || "-"}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color={item.is_active ? "success.main" : "error.main"}>
                                                    {item.is_active ? "Active" : "Inactive"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="primary" onClick={() => {
                                                    setCurrentBreakdown(item);
                                                    setOpenBreakdownDialog(true);
                                                }}>
                                                    <RefreshIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteBreakdown(item.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Breakdown Dialog */}
            <AppDialog open={openBreakdownDialog} onClose={() => setOpenBreakdownDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: "bold" }}>
                    {currentBreakdown.id ? "Edit Investment Item" : "Add Investment Item"}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                                <InputLabel>Tax Section</InputLabel>
                                <Select
                                    value={currentBreakdown.section}
                                    label="Tax Section"
                                    onChange={(e) => setCurrentBreakdown({ ...currentBreakdown, section: e.target.value })}
                                >
                                    {(sections["INVESTMENT_TYPES"] || []).length > 0 ? (
                                        sections["INVESTMENT_TYPES"].map((item) => {
                                            const key = item.key.replace("INVESTMENT_TYPES_", "");
                                            const finalKey = key === "HOMELOAN" ? "homeLoanInterest" :
                                                key === "OTHER" ? "otherDeductions" : key;
                                            const label = item.description?.split('(')[0]?.trim() || finalKey;
                                            return <MenuItem key={finalKey} value={finalKey}>{label}</MenuItem>;
                                        })
                                    ) : (
                                        // Fallback to core sections if none defined in constants yet
                                        ["80C", "80D", "HRA", "homeLoanInterest", "otherDeductions", "NPS"].map(s => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Item Name (e.g. PPF, LIC)"
                                value={currentBreakdown.item_name}
                                onChange={(e) => setCurrentBreakdown({ ...currentBreakdown, item_name: e.target.value })}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Description (Optional)"
                                value={currentBreakdown.description}
                                onChange={(e) => setCurrentBreakdown({ ...currentBreakdown, description: e.target.value })}
                                variant="outlined"
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={currentBreakdown.is_active}
                                        onChange={(e) => setCurrentBreakdown({ ...currentBreakdown, is_active: e.target.checked })}
                                    />
                                }
                                label="Is Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenBreakdownDialog(false)} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSaveBreakdown}
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                    >
                        {currentBreakdown.id ? "Update Item" : "Create Item"}
                    </Button>
                </DialogActions>
            </AppDialog>
        </Box>
    );
};

export default PayrollSettings;